'use client';

import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faStethoscope,
  faRightFromBracket,
} from '@fortawesome/pro-solid-svg-icons';

const options = [
  { label: 'Treating', value: 'treating', icon: faStethoscope },
  { label: 'Referring', value: 'sending', icon: faRightFromBracket },
] as const;

const DEFAULT_DISABLED_TOOLTIP = 'This option is disabled';

export interface AnalyticsModeToggleProps {
  value: 'treating' | 'sending';
  onChange: (value: 'treating' | 'sending') => void;
  isDisabled?: (value: string) => boolean;
  /** Copy shown in the tooltip when a hovered option is disabled. */
  disabledTooltip?: string;
}

const AnalyticsModeToggle: React.FC<AnalyticsModeToggleProps> = ({
  value,
  onChange,
  isDisabled,
  disabledTooltip = DEFAULT_DISABLED_TOOLTIP,
}): React.ReactElement => {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleClick = useCallback(
    (newValue: 'treating' | 'sending') => {
      if (isDisabled?.(newValue)) return;
      onChange(newValue);
    },
    [isDisabled, onChange]
  );

  return (
    <div
      className="relateive z-10 rounded-full border border-neutral-200 flex justify-center items-center bg-white relative h-9"
      data-testid="analytics-volume-toggle-container"
    >
      <div
        className="absolute h-full w-1/2 transition-all duration-300 ease-in-out rounded-full bg-sky-600"
        style={{
          left: value === 'treating' ? '0%' : '50%',
        }}
      />

      {options.map((option, idx) => {
        const isOptionDisabled = isDisabled?.(option.value);
        const isActive = value === option.value;

        return (
          <div
            key={`${option.value}-${idx}`}
            className="w-1/2 rounded-full flex justify-center items-center z-10 h-full relative px-4"
            data-testid={`${option.value}-option-container`}
            onMouseEnter={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setTooltipPosition({
                top: rect.top - 20,
                left: rect.left + rect.width / 2,
              });
              setShowTooltip(option.value);
            }}
            onMouseLeave={() => setShowTooltip(null)}
          >
            <button
              type="button"
              data-testid={`${option.value}-button`}
              className={`w-full flex justify-center items-center focus:outline-none ${
                isOptionDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer'
              }`}
              aria-pressed={isActive}
              onClick={() => handleClick(option.value)}
              tabIndex={isOptionDisabled ? -1 : 0}
              disabled={isOptionDisabled}
            >
              <div
                className={`w-full text-center ${
                  isActive
                    ? 'text-white font-bold'
                    : 'text-secondary font-normal'
                } text-sm font-['Inter'] leading-tight transition-colors duration-300 flex items-center justify-center gap-1`}
              >
                <Icon icon={option.icon} className="text-xs" />
                <span>{option.label}</span>
              </div>
            </button>
          </div>
        );
      })}

      {/* Portal for tooltips */}
      {showTooltip &&
        document.body &&
        ReactDOM.createPortal(
          options.map((option) => {
            const isOptionDisabled = isDisabled?.(option.value);
            return (
              <div
                key={`tooltip-${option.value}`}
                data-testid={`${option.value}-tooltip`}
                data-visible={showTooltip === option.value && isOptionDisabled}
                style={{
                  position: 'fixed',
                  top: `${tooltipPosition.top}px`,
                  left: `${tooltipPosition.left}px`,
                  transform: 'translateX(-50%)',
                  zIndex: 9999,
                  display:
                    showTooltip === option.value && isOptionDisabled
                      ? 'block'
                      : 'none',
                }}
                className={`px-2 py-1 text-xs bg-neutral-900 text-white font-medium rounded-lg shadow-lg
              pointer-events-none transition-opacity ${
                showTooltip === option.value && isOptionDisabled
                  ? 'opacity-100'
                  : 'opacity-0'
              }`}
              >
                {disabledTooltip}
                <div className="absolute w-2 h-2 bg-neutral-900 -bottom-1 left-1/2 -translate-x-1/2 rotate-45" />
              </div>
            );
          }),
          document.body
        )}
    </div>
  );
};

export default AnalyticsModeToggle;
